import React from "react";
import { Mail, MapPin, Phone } from "lucide-react";
import { ButtonLink } from "../../components/ui/button";
import { LinkAnchor } from "../../components/ui/link";
import { cn } from "../../lib/utils";
import { resolveContractorSiteThemeClass } from "../ContractorSitePage/siteCustomization";
import { CONTRACTOR_SURFACE_RADIUS_MD_CLASS } from "../ContractorSitePage/layoutRadius";
import { AppToastProvider } from "../../components/feedback/AppToastProvider";
import FlashToaster from "../../components/feedback/FlashToaster";
import type { ContractorSeoSharedProps } from "./types";

type ContractorSeoLayoutProps = ContractorSeoSharedProps & {
  children: React.ReactNode;
};

export const ContractorSeoLayout: React.FC<ContractorSeoLayoutProps> = ({
  business,
  rootUrl,
  currentYear,
  seoLinks,
  flash,
  children,
}) => {
  const themeClassName = resolveContractorSiteThemeClass(
    business.themeVariant,
    business.hasPublishedDesignTokens,
  );

  return (
    <div className={cn("contractor-site-root", themeClassName)}>
      <AppToastProvider>
        {flash ? <FlashToaster flash={flash} /> : null}
        <header className="border-b border-border bg-card/95 backdrop-blur">
          <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-8">
            <LinkAnchor
              href="/"
              className="flex items-center gap-2 hover:no-underline"
            >
              {business.logoUrl ? (
                <img
                  src={business.logoUrl}
                  alt={business.businessName}
                  className="h-9 w-9 object-contain"
                />
              ) : (
                <div
                  className={cn(
                    "flex h-9 w-9 items-center justify-center bg-accent",
                    CONTRACTOR_SURFACE_RADIUS_MD_CLASS,
                  )}
                >
                  <span className="font-display text-lg font-semibold text-accent-foreground">
                    {business.businessName.charAt(0)}
                  </span>
                </div>
              )}
              <span className="font-display text-xl font-semibold text-foreground">
                {business.businessName}
              </span>
            </LinkAnchor>
            <nav className="hidden items-center gap-6 md:flex">
              <LinkAnchor
                href={seoLinks.services}
                className="text-sm font-medium text-muted-foreground hover:text-accent hover:no-underline"
              >
                Services
              </LinkAnchor>
              <LinkAnchor
                href={seoLinks.areas}
                className="text-sm font-medium text-muted-foreground hover:text-accent hover:no-underline"
              >
                Service Areas
              </LinkAnchor>
              <LinkAnchor
                href={seoLinks.faq}
                className="text-sm font-medium text-muted-foreground hover:text-accent hover:no-underline"
              >
                FAQ
              </LinkAnchor>
            </nav>
            <div className="flex items-center gap-3">
              <LinkAnchor
                href={`tel:${business.phone}`}
                className="hidden items-center gap-2 text-sm font-semibold text-primary hover:no-underline sm:flex"
              >
                <Phone className="h-4 w-4 shrink-0" />
                {business.phone}
              </LinkAnchor>
              <ButtonLink href="/#contact" size="sm">
                Get a Quote
              </ButtonLink>
            </div>
          </div>
        </header>

        <main className="bg-background">{children}</main>

        <footer className="border-t border-border bg-card text-foreground">
          <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
              <div className="sm:col-span-2 lg:col-span-1">
                <p className="font-display text-lg font-semibold text-foreground">
                  {business.businessName}
                </p>
                <p className="mt-2 text-sm text-muted-foreground">
                  {business.tradeDisplayName ?? "Contractor"} serving{" "}
                  {business.serviceArea}.
                </p>
              </div>
              <div>
                <h4 className="mb-3 font-display text-sm font-semibold text-foreground">
                  Explore
                </h4>
                <ul className="space-y-2">
                  <li>
                    <LinkAnchor
                      href="/"
                      className="text-sm text-muted-foreground hover:text-accent hover:no-underline"
                    >
                      Home
                    </LinkAnchor>
                  </li>
                  <li>
                    <LinkAnchor
                      href={seoLinks.services}
                      className="text-sm text-muted-foreground hover:text-accent hover:no-underline"
                    >
                      Services
                    </LinkAnchor>
                  </li>
                  <li>
                    <LinkAnchor
                      href={seoLinks.areas}
                      className="text-sm text-muted-foreground hover:text-accent hover:no-underline"
                    >
                      Service Areas
                    </LinkAnchor>
                  </li>
                  <li>
                    <LinkAnchor
                      href={seoLinks.faq}
                      className="text-sm text-muted-foreground hover:text-accent hover:no-underline"
                    >
                      FAQ
                    </LinkAnchor>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="mb-3 font-display text-sm font-semibold text-foreground">
                  Contact
                </h4>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2">
                    <Phone className="h-4 w-4 shrink-0 text-accent" />
                    <LinkAnchor
                      href={`tel:${business.phone}`}
                      className="text-sm text-muted-foreground hover:no-underline"
                    >
                      {business.phone}
                    </LinkAnchor>
                  </li>
                  {business.businessEmail ? (
                    <li className="flex items-center gap-2">
                      <Mail className="h-4 w-4 shrink-0 text-accent" />
                      <LinkAnchor
                        href={`mailto:${business.businessEmail}`}
                        className="text-sm text-muted-foreground hover:no-underline"
                      >
                        {business.businessEmail}
                      </LinkAnchor>
                    </li>
                  ) : null}
                  <li className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 shrink-0 text-accent" />
                    <span className="text-sm text-muted-foreground">
                      {business.serviceArea}
                    </span>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="mb-3 font-display text-sm font-semibold text-foreground">
                  Get Started
                </h4>
                <ButtonLink href="/#contact" className="w-full">
                  Request a Quote
                </ButtonLink>
              </div>
            </div>
            <div className="mt-10 flex flex-col items-center justify-between gap-3 border-t border-border pt-6 sm:flex-row">
              <p className="text-xs text-muted-foreground">
                &copy; {currentYear} {business.businessName}. All rights
                reserved.
              </p>
              <LinkAnchor
                href={rootUrl}
                className="text-xs text-muted-foreground hover:text-foreground hover:no-underline"
              >
                Website by ToolKit
              </LinkAnchor>
            </div>
          </div>
        </footer>
      </AppToastProvider>
    </div>
  );
};
